/**
 * Phase 2 home transfer: the buyer chooses a sponsor, and only after the
 * seller confirms closing. CommonJS only. No object spread.
 *
 * Transfers turn on when HOME_HISTORY_TRANSFER is "on", or when the Supabase
 * URL is the DEV project. Functions do not see netlify.toml context variables.
 * LIVE stays off unless the variable is "on".
 */

const { PACARION_WORDMARK } = require('./sponsorlessBrand');

const HOME_HISTORY_TRANSFER_FLAG = 'HOME_HISTORY_TRANSFER';
const LIVE_PROJECT_REF = 'zvnubjrizoiwrzrhunjz';
const DEV_PROJECT_REF = 'ftrhhpaduufkjfkjwzru';

const TRANSFER_TEXT_VERSION = '2026-10-21.home_transfer.v1';
const CLAIM_TEXT_VERSION = '2026-10-21.home_claim.v1';
const TRANSFER_CONSENT_TYPE = 'home_transfer';
const CLAIM_CONSENT_TYPE = 'home_claim';

const SELLER_KEEP_NOTE = 'Your account stays yours. The buyer gets a separate copy of the report you chose.';
const FLAG_OFF_ERROR = 'Home transfers are not available yet.';
const CLOSING_ERROR = 'Confirm closing only after the sale has closed.';
const SPONSOR_REQUIRED_ERROR = 'Choose a sponsor, or continue without one.';
const SELLER_AGENT_ERROR = 'Choose your own sponsor, or continue without one. The previous owner\'s sponsor isn\'t linked to this home.';
const SPONSOR_BLOCKED_ERROR = 'That sponsor can\'t be chosen for a claimed home.';
const INSURANCE_STATE_ERROR = 'An insurance sponsor can be chosen only for a Florida home.';
const TEST_INBOX_ERROR = 'Buyer invites can only be sent to a Resend test inbox (delivered+tag@resend.dev).';
const SELLER_EMAIL_ERROR = 'Use the buyer\'s email. It can\'t be the seller\'s email.';
const BUYER_EMAIL_ERROR = 'Enter the buyer\'s email.';
const CLAIM_LINK_DAYS = 30;
const CLAIM_EXPIRED_ERROR = 'This claim link has expired.';
const CONTACT_STRIP_NOTE = 'Phone numbers and email addresses written in the report are left out of the buyer\'s copy.';

const BLOCKED_SPONSOR_ROLES = {
  title: true,
  inspector: true,
  lender: true,
  vendor: true,
  admin: true,
  super_admin: true,
  homeowner: true,
  team_member: true,
};

const REAL_ESTATE_ROLES = { agent: true, member: true, owner: true };

function homeTransferEnabled(env) {
  const source = env || {};
  if (String(source[HOME_HISTORY_TRANSFER_FLAG] || '').toLowerCase() === 'on') return true;
  const url = String(source.SUPABASE_URL || source.REACT_APP_SUPABASE_URL || '');
  return url.indexOf(DEV_PROJECT_REF) !== -1;
}

function clean(value, max) {
  return String(value == null ? '' : value).replace(/[\r\n]+/g, ' ').trim().slice(0, max || 160);
}

function isoDate(now) {
  const date = now instanceof Date ? now : new Date(now || Date.now());
  const y = date.getUTCFullYear();
  const m = String(date.getUTCMonth() + 1);
  const d = String(date.getUTCDate());
  const month = m.length === 1 ? '0' + m : m;
  const day = d.length === 1 ? '0' + d : d;
  return y + '-' + month + '-' + day;
}

function closingDecision(dateString, now) {
  const value = String(dateString || '').trim();
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) {
    return { ok: false, status: 400, error: 'Enter the closing date.' };
  }
  if (value > isoDate(now || new Date())) {
    return { ok: false, status: 400, error: CLOSING_ERROR };
  }
  return { ok: true, closingDate: value };
}

function transferTestRecipient(email) {
  const value = String(email || '').trim().toLowerCase();
  if (/^delivered\+[a-z0-9._-]+@resend\.dev$/.test(value)) return value;
  return '';
}

function devProjectUrl(env) {
  const source = env || {};
  const url = String(source.SUPABASE_URL || source.REACT_APP_SUPABASE_URL || '');
  return url.indexOf(DEV_PROJECT_REF) !== -1;
}

function buyerEmailDecision(email, env, sellerEmail) {
  const value = String(email || '').trim().toLowerCase();
  const seller = String(sellerEmail || '').trim().toLowerCase();
  const at = value.indexOf('@');
  const domain = at === -1 ? '' : value.slice(at + 1);
  const shaped = at > 0 && value.indexOf(' ') === -1 && domain.indexOf('.') > 0 && domain.indexOf('.') < domain.length - 1;
  if (!shaped || value.length > 254) {
    return { ok: false, status: 400, error: BUYER_EMAIL_ERROR };
  }
  if (seller && value === seller) {
    return { ok: false, status: 400, error: SELLER_EMAIL_ERROR };
  }
  if (devProjectUrl(env)) {
    if (!transferTestRecipient(value)) {
      return { ok: false, status: 400, error: TEST_INBOX_ERROR };
    }
  }
  return { ok: true, email: value };
}

function countItems(snapshot, sectionId) {
  const sections = (snapshot && snapshot.sections) || [];
  let count = 0;
  sections.forEach((section) => {
    if (section && section.id === sectionId) count += (section.items || []).length;
  });
  return count;
}

function transferConsentNotice(report) {
  const source = report || {};
  const number = clean(source.reportNumber, 40) || 'the report';
  const version = Math.floor(Number(source.version)) > 0 ? Math.floor(Number(source.version)) : 1;
  const docs = Math.max(0, Math.floor(Number(source.documentCount) || 0));
  const photos = Math.max(0, Math.floor(Number(source.photoCount) || 0));
  return [
    'Hand off this home\'s history to the buyer',
    'The buyer will get a copy of report ' + number + ' v' + version + ' in their own Pacarion account: house facts, systems, the maintenance history in this report, and the ' + docs + ' documents and ' + photos + ' photos you included. They won\'t get your AI conversations, private notes, saved pros, contacts, insurance details, or anything you didn\'t include. Your own account doesn\'t change.',
    'I\'m an owner of this home (or authorized by all owners), and the sale closed on the date I entered. I give the buyer, later owners of the home, and Pacarion a free, permanent, irrevocable, non-exclusive license to use, copy, and display this copy for the home. I understand that after the buyer claims it, deleting my account won\'t delete the buyer\'s copy, but Pacarion will remove my name and other personal information from it if I ask. I can cancel this hand-off any time before the buyer claims it.',
  ].join('\n\n');
}

function claimConsentNotice(addressLabel) {
  const place = clean(addressLabel, 180) || 'this home';
  return [
    'Claim this home\'s history',
    'You\'re getting a copy of the previous owner\'s records for ' + place + '. The previous owner recorded these records and chose what to share. Pacarion didn\'t check them. They are not a seller\'s disclosure, an inspection, or a warranty, and they may be incomplete or wrong.',
    'I am 18 or older and agree to the Terms of Service (including the arbitration agreement and class action waiver, which I may opt out of within 30 days), and I have read the Privacy Policy. I understand the previous owner\'s history is shown as they shared it, and I won\'t rely on it instead of my own inspection or the seller\'s disclosures.',
  ].join('\n\n');
}

function previousOwnerNote(closingDate) {
  const date = clean(closingDate, 20) || 'closing';
  return 'History before ' + date + ' was recorded by the previous owner and is shown as they shared it.';
}

function buyerFacts(client) {
  const source = client || {};
  const state = clean(source.state, 20).toUpperCase();
  return {
    address: clean(source.address, 200),
    city: clean(source.city, 80),
    state: /^[A-Z]{2}$/.test(state) ? state : '',
    zip_code: String(source.zip_code || '').replace(/\D/g, '').slice(0, 5),
    property_type: clean(source.property_type, 40),
    year_built: yearNumber(source.year_built),
    square_feet: yearNumber(source.square_feet),
  };
}

function yearNumber(value) {
  const number = Math.floor(Number(value));
  if (!number || number < 0) return null;
  return number;
}

function dropUnmatchedParen(text) {
  const open = text.indexOf('(');
  const close = text.lastIndexOf(')');
  if (open === -1 && close === -1) return text;
  if (open === -1) return text.slice(0, close) + ' ' + text.slice(close + 1);
  if (close === -1 || close < open) return text.slice(0, open) + ' ' + text.slice(open + 1);
  return text;
}

function tidyContactFragments(value) {
  let text = String(value || '');
  let previous = '';
  while (text !== previous) {
    previous = text;
    text = text.replace(/\s+/g, ' ').trim();
    text = text.replace(/\(\s*\)/g, ' ');
    text = dropUnmatchedParen(text);
    text = text.replace(/#(?=\s|$)/g, ' ');
    text = text.replace(/\b(?:and|or|at)\s+(?:and|or|at)\b/gi, ' ');
    text = text.replace(/^(?:and|or|at)\b\s*/i, '');
    text = text.replace(/\s+\b(?:and|or|at)\b\s*$/i, '');
    text = text.replace(/\s+/g, ' ').trim();
    text = text.replace(/^[·•—–−,;:|/\s()\-+#]+|[·•—–−,;:|/\s()\-+#]+$/g, '').trim();
  }
  return text;
}

function stripBarePhones(text) {
  return text.replace(/(^|[^A-Za-z0-9#])(\+?1[\s.-]?)?(\d{10,11})(?!\d)/g, (match, lead, prefix, digits) => {
    if (digits.length === 10) return lead;
    if (digits.length === 11 && digits.charAt(0) === '1' && !prefix) return lead;
    return match;
  });
}

function stripContacts(value) {
  let text = clean(value, 400);
  text = text.replace(/[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/gi, ' ');
  text = text.replace(/(?:\+?1[\s.-])?(?:\(\d{3}\)[\s.-]?|\d{3}[\s.-])\d{3}[\s.-]\d{4}/g, ' ');
  text = stripBarePhones(text);
  return tidyContactFragments(text);
}

const DUE_TIMING = /(?:\s*[·•]\s*|\s+-\s+)?(?:\d+\s+days?\s+after\s+the\s+due\s+date|before\s+the\s+due\s+date|on\s+time)\s*$/i;

function stripDueTiming(value) {
  return String(value == null ? '' : value).replace(DUE_TIMING, '').trim();
}

function sharedText(value) {
  const original = clean(value, 400);
  const text = stripContacts(original);
  return { text: text, removed: text !== original };
}

function historyFromSnapshot(snapshot, closingDate) {
  const entries = [];
  let contactsRemoved = 0;
  const sections = (snapshot && snapshot.sections) || [];
  sections.forEach((section) => {
    if (!section) return;
    (section.items || []).forEach((item) => {
      if (!item) return;
      const title = sharedText(item.title || section.name);
      let detailSource = item.detail;
      if (section.id === 'maintenance') {
        const report = require('./homeHistoryReport');
        if (!report.reportKeepsDueTiming(item)) detailSource = stripDueTiming(item.detail);
      }
      const detail = sharedText(detailSource);
      if (title.removed || detail.removed || item.note || item.phone || item.email) contactsRemoved += 1;
      entries.push({
        kind: clean(section.id, 40) || 'report',
        title: title.text || 'Record',
        detail: detail.text,
        readOnly: true,
        closingDate: closingDate,
      });
    });
  });
  return { entries: entries, contactsRemoved: contactsRemoved };
}

function claimExpiresAt(now) {
  const date = now instanceof Date ? now : new Date(now || Date.now());
  return new Date(date.getTime() + CLAIM_LINK_DAYS * 24 * 60 * 60 * 1000).toISOString();
}

function claimExpired(transfer, now) {
  const moment = now instanceof Date ? now : new Date(now || Date.now());
  const stamp = transfer && transfer.expires_at;
  if (stamp) return new Date(stamp).getTime() <= moment.getTime();
  const created = transfer && transfer.created_at;
  if (!created) return true;
  return new Date(created).getTime() + CLAIM_LINK_DAYS * 24 * 60 * 60 * 1000 <= moment.getTime();
}

function transferPackage(seller, snapshot, closingDate) {
  const history = historyFromSnapshot(snapshot, closingDate);
  return {
    facts: buyerFacts(seller),
    entries: history.entries,
    contactsRemoved: history.contactsRemoved,
    previousOwnerNote: previousOwnerNote(closingDate),
    sellerPatch: null,
  };
}

function packageLeaks(pkg, secrets) {
  const text = JSON.stringify(pkg);
  const list = secrets || [];
  for (let index = 0; index < list.length; index += 1) {
    const secret = String(list[index] || '');
    if (secret && text.indexOf(secret) !== -1) return secret;
  }
  return '';
}

function copiedAssetPaths(assets, transferId) {
  const files = (assets && assets.files) || {};
  const plan = [];
  Object.keys(files).forEach((id) => {
    const file = files[id];
    if (!file || !file.path) return;
    plan.push({
      id: id,
      from: String(file.path),
      to: 'transfers/' + clean(transferId, 80) + '/' + clean(id, 80),
      name: clean(file.name, 120) || 'File',
    });
  });
  return plan;
}

function sponsorCodeValue(value) {
  return String(value == null ? '' : value).replace(/[\r\n]+/g, ' ').trim().toUpperCase().slice(0, 6);
}

function sponsorInviteUsable(invite, now) {
  if (!invite || !invite.agent_id) return false;
  if (invite.accepted_at) return false;
  if (!invite.expires_at) return false;
  const moment = now instanceof Date ? now : new Date(now || Date.now());
  const expires = new Date(invite.expires_at).getTime();
  if (Number.isNaN(expires)) return false;
  return expires > moment.getTime();
}

function buyerSponsorDecision(input) {
  const source = input || {};
  const choice = source.choice;
  if (choice !== 'sponsorless' && choice !== 'code') {
    return { ok: false, status: 400, error: SPONSOR_REQUIRED_ERROR };
  }
  if (choice === 'sponsorless') {
    return { ok: true, agentId: null, choice: 'sponsorless' };
  }
  const code = sponsorCodeValue(source.code);
  if (!/^[A-Z0-9]{6}$/.test(code)) {
    return { ok: false, status: 400, error: 'Enter your sponsor\'s 6-character code.' };
  }
  const profile = source.profile;
  if (!profile || !profile.id) {
    return { ok: false, status: 400, error: 'That sponsor code is not active.' };
  }
  const sellerIds = source.sellerAgentIds || [];
  if (sellerIds.indexOf(profile.id) !== -1) {
    return { ok: false, status: 403, error: SELLER_AGENT_ERROR };
  }
  const role = String(profile.role || '');
  const homeState = clean(source.homeState, 2).toUpperCase();
  if (role === 'insurance') {
    if (homeState !== 'FL') return { ok: false, status: 403, error: INSURANCE_STATE_ERROR };
    return { ok: true, agentId: profile.id, choice: 'code' };
  }
  if (BLOCKED_SPONSOR_ROLES[role] || !REAL_ESTATE_ROLES[role]) {
    return { ok: false, status: 403, error: SPONSOR_BLOCKED_ERROR };
  }
  return { ok: true, agentId: profile.id, choice: 'code' };
}

function claimAllowed(transfer, now) {
  if (!transfer || transfer.status !== 'invited') {
    return { ok: false, status: 403, error: 'This hand-off is no longer available.' };
  }
  if (!transfer.closing_confirmed_at || !transfer.closing_date) {
    return { ok: false, status: 403, error: 'The seller has not recorded closing.' };
  }
  if (claimExpired(transfer, now)) {
    return { ok: false, status: 403, error: CLAIM_EXPIRED_ERROR };
  }
  return { ok: true };
}

function claimFromPath(pathname) {
  const path = String(pathname || '').split('?')[0].split('#')[0].replace(/\/+$/, '');
  const match = /^\/claim-home\/([A-Za-z0-9_-]{22,128})$/.exec(path);
  if (!match) return null;
  return { token: match[1] };
}

module.exports = {
  PACARION_WORDMARK,
  HOME_HISTORY_TRANSFER_FLAG,
  LIVE_PROJECT_REF,
  DEV_PROJECT_REF,
  TRANSFER_TEXT_VERSION,
  CLAIM_TEXT_VERSION,
  TRANSFER_CONSENT_TYPE,
  CLAIM_CONSENT_TYPE,
  SELLER_KEEP_NOTE,
  FLAG_OFF_ERROR,
  CLOSING_ERROR,
  SPONSOR_REQUIRED_ERROR,
  SELLER_AGENT_ERROR,
  TEST_INBOX_ERROR,
  SELLER_EMAIL_ERROR,
  BUYER_EMAIL_ERROR,
  CLAIM_LINK_DAYS,
  CLAIM_EXPIRED_ERROR,
  CONTACT_STRIP_NOTE,
  homeTransferEnabled,
  closingDecision,
  transferTestRecipient,
  devProjectUrl,
  buyerEmailDecision,
  countItems,
  transferConsentNotice,
  claimConsentNotice,
  previousOwnerNote,
  buyerFacts,
  stripContacts,
  stripDueTiming,
  historyFromSnapshot,
  claimExpiresAt,
  claimExpired,
  transferPackage,
  packageLeaks,
  copiedAssetPaths,
  sponsorCodeValue,
  sponsorInviteUsable,
  buyerSponsorDecision,
  claimAllowed,
  claimFromPath,
};
